import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, Trophy, CheckCircle, Handshake, ExternalLink, ChevronRight } from 'lucide-react';
import { Logo } from '../components/Logo';

const ScrollReveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('revealed'); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`scroll-reveal ${delay ? `delay-${delay}` : ''} ${className}`}>
      {children}
    </div>
  );
};

const VALUE_ICONS = [Shield, Trophy, CheckCircle, Handshake];

export default function About() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const values = t('about.values', { returnObjects: true });

  const handleQuote = () => {
    navigate('/contact');
  };

  return (
    <div className="min-h-screen bg-white">

      {/* HERO BANNER */}
      <section
        className="relative pt-24 pb-10 md:pt-32 md:pb-16"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/2402777/pexels-photo-2402777.jpeg?auto=compress&cs=tinysrgb&w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
        }}
        data-testid="about-hero"
      >
        <div className="absolute inset-0 bg-[#1B4F6B]/90" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-1 h-12 bg-[#F5820D]" />
            <div>
              <h1 className="font-oswald font-bold text-white uppercase leading-none" style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }}>
                {t('about.title')}
              </h1>
              <p className="font-manrope text-white/70 text-base mt-2">{t('about.subtitle')}</p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="w-full h-10">
            <path d="M0 40 L0 20 L360 5 L720 25 L1080 5 L1440 20 L1440 40 Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* STORY */}
      <section className="py-10 md:py-20 bg-white" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-14 items-center">
            <ScrollReveal>
              <div>
                <div className="flex items-center gap-4 mb-3 md:mb-6">
                  <div className="w-1 h-10 bg-[#C0392B]" />
                  <h2 className="font-oswald text-2xl md:text-4xl font-bold uppercase text-[#111111]">
                    {t('about.story_title')}
                  </h2>
                </div>
                <p className="font-manrope text-gray-700 text-sm md:text-base leading-relaxed">
                  {t('about.story_text')}
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div className="relative">
                <div className="bg-[#1B4F6B] rounded-sm p-3 md:p-8 text-center">
                  <Logo size="md" className="mx-auto mb-3 md:mb-6 !h-36 w-auto" variant="white" />
                  <div className="grid grid-cols-3 gap-2 md:gap-4 mt-3 md:mt-6">
                    {[
                      { num: '18+', label: 'Événements' },
                      { num: '2020', label: 'Fondé' },
                      { num: 'BE+FR', label: 'Territoire' },
                    ].map((stat, i) => (
                      <div key={i} className="text-center">
                        <div className="font-oswald text-[#F5820D] text-xl md:text-2xl font-bold">{stat.num}</div>
                        <div className="font-manrope text-white/60 text-xs uppercase tracking-wider mt-0.5">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="absolute -bottom-3 -right-3 w-full h-full border-2 border-[#F5820D]/30 rounded-sm -z-10" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-10 md:py-20 bg-[#F8F9FA]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="text-center mb-6 md:mb-14">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-1 h-10 bg-[#F5820D]" />
                <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase text-[#111111]">
                  {t('about.values_title')}
                </h2>
                <div className="w-1 h-10 bg-[#F5820D]" />
              </div>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {Array.isArray(values) && values.map((val, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <ScrollReveal key={i} delay={i * 100}>
                  <div className="bg-white border border-gray-200 rounded-sm p-3 md:p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex flex-col" data-testid={`value-card-${i}`}>
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-[#1B4F6B]/10 rounded-sm flex items-center justify-center mb-2 md:mb-4">
                      {Icon && <Icon size={16} className="text-[#1B4F6B]" />}
                    </div>
                    <h3 className="font-oswald text-sm md:text-base font-bold uppercase text-[#111111] mb-1.5 md:mb-3 tracking-wide">
                      {val.title}
                    </h3>
                    <p className="font-manrope text-gray-600 text-xs md:text-sm leading-relaxed flex-1">
                      {val.text}
                    </p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP */}
      <section
        className="py-10 md:py-20 relative"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/2402777/pexels-photo-2402777.jpeg?auto=compress&cs=tinysrgb&w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="partnership-section"
      >
        <div className="absolute inset-0 bg-[#0a0a0a]/65" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <ScrollReveal>
            <span className="inline-block border border-[#F5820D]/40 text-[#F5820D] font-oswald uppercase tracking-widest text-xs px-4 py-1.5 mb-3 md:mb-6">
              Partnership
            </span>
            <h2 className="font-oswald text-2xl md:text-4xl font-bold uppercase text-white mb-3 md:mb-6">
              {t('about.partnership_title')}
            </h2>
            <p className="font-manrope text-white/95 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-4 md:mb-8">
              {t('about.partnership_text')}
            </p>
            <a
              href="https://ultratiming.live"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#F5820D] hover:bg-[#D9730B] text-white font-oswald uppercase tracking-widest text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
              data-testid="partnership-link"
            >
              {t('about.partnership_cta')} <ExternalLink size={15} />
            </a>
          </ScrollReveal>
        </div>
      </section>

      {/* CLIENT LOGOS */}
      <section className="py-10 md:py-20 bg-white" data-testid="about-clients-section">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="text-center mb-6 md:mb-12">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-1 h-10 bg-[#F5820D]" />
                <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase text-[#111111]">
                  {t('about.clients_title')}
                </h2>
                <div className="w-1 h-10 bg-[#F5820D]" />
              </div>
              <p className="font-manrope text-gray-600 text-sm md:text-base">{t('about.clients_subtitle')}</p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <div className="rounded-sm overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_trail-event-shop/artifacts/xeguco0i_Screenshot%202026-02-23%20at%2020.24.44.png"
                alt="Nos clients partenaires"
                className="w-full h-auto"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-20 bg-[#111111]" data-testid="about-cta-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <ScrollReveal>
            <h2 className="font-oswald text-2xl md:text-4xl font-bold uppercase text-white mb-2 md:mb-4">
              {t('about.cta_title')}
            </h2>
            <p className="font-manrope text-white/60 text-sm md:text-base mb-5 md:mb-8 max-w-xl mx-auto">
              {t('about.cta_text')}
            </p>
            <button
              onClick={handleQuote}
              className="inline-flex items-center gap-2 bg-[#F5820D] hover:bg-[#D9730B] text-white font-oswald uppercase tracking-widest text-sm px-10 py-4 transition-all duration-300 hover:-translate-y-0.5"
              data-testid="about-cta-btn"
            >
              {t('about.cta_button')} <ChevronRight size={15} />
            </button>
          </ScrollReveal>
        </div>
      </section>

    </div>
  );
}
